import get from "lodash/get";
import { useMemo, useState } from "react";
import styled from "styled-components";

import { faChevronDown, faChevronUp, faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import LoadingAnimation from "components/organisms/LoadingAnimation";

const TableRoot = styled.table`
	position: relative;
	border-collapse: collapse;

	tr {
		border: 1px solid #707c9740;
		border-width: 1px 0;
	}

	tr:last-child {
		border-top: none;
	}

	th {
		text-align: left;
		padding: 0.5rem 1rem;
		font-weight: bold;
	}

	td {
		padding: 0.5rem 1rem;
	}
`;

const TableHeaderButton = styled.button`
	font-size: 0.875rem;
	font-weight: 700;
	display: flex;
	gap: 0.5rem;
	align-items: center;
	border: none;
	background: none;
	color: inherit;
	padding: 0 0.3rem;

	&:hover {
		background: transparent;
		color: ${(props) => props.theme.colors.text};
		border: 0;
	}
`;

const TableHeaderIcon = styled(FontAwesomeIcon)<{ $active: boolean }>`
	font-size: 0.8rem;
	color: ${(props) => (!props.$active ? "var(--nte-graa-mork)" : "var(--nte-blaa-logo)")};
`;

/**
 * Table creates a table with sortable columns based on the objects and labels provided.
 * This component requires a type parameter T, which is the type of the table entry objects.
 * To include a property of the object in the table, add a label for it using `labels={ key: "Label" }`.
 * Nested properties can also be added by using string syntax: `labels={ "key.subkey": "Label" }`.
 * If values need to be generated before displaying, add a generator function in the same way as labels: `generators={ key: (v: T) => v }`.
 * If values need to be sorted in a specific way, add a sort function in the same way as labels: `sorters={ key: (a: any, b: any) => number }`.
 * To add an action to each row, use the action prop to pass a function: `action={(entry: T) => { <Button>Edit {entry.name}</Button> }}`
 */
export function Table<T>({
	entries = [],
	labels,
	generators,
	sorters,
	action,
	loading,
	error,
}: {
	readonly entries?: T[];
	readonly labels: Partial<{ [key in keyof T]: string }> | { [key in string]: string };
	readonly generators?: Partial<{ [key in keyof T]: (v: T) => React.ReactNode }> | { [key in string]: (v: T) => React.ReactNode };
	readonly sorters?: Partial<{ [key in keyof T]: (a: unknown, b: unknown) => number }> | { [key in string]: (a: unknown, b: unknown) => number };
	readonly action?: (v: T) => React.ReactNode;
	readonly loading?: boolean;
	readonly error?: boolean;
}) {
	const [sortedBy, setSortedBy] = useState<string>();
	const [reversed, setReversed] = useState(false);

	const sortedEntries = useMemo(() => {
		if (!sortedBy) return [...entries];

		/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */
		const customSorterForKey = sorters ? (sorters[sortedBy] as (a: unknown, b: unknown) => number) : false;

		if (customSorterForKey)
			return [...entries].sort((a, b) => {
				const result = customSorterForKey(get(a, sortedBy), get(b, sortedBy));
				return reversed ? -result : result;
			});

		return [...entries].sort((a, b) => {
			const result = get(a, sortedBy) < get(b, sortedBy) ? -1 : 1;
			return reversed ? -result : result;
		});
	}, [entries, sortedBy, reversed, sorters]);

	// This function toggles through Inactive -> Active -> Active (reverse) -> Inactive
	const headerOnClick = (key: string) => {
		if (sortedBy === key) {
			if (reversed) {
				setSortedBy(undefined);
				setReversed(false);
			} else {
				setReversed(true);
			}
		} else {
			setSortedBy(key);
			setReversed(false);
		}
	};

	const labelKeys = Object.keys(labels);

	return (
		<TableRoot>
			<thead>
				<tr>
					{/* For every labelled key, create a table header that can be clicked to sort columns */}
					{labelKeys.map((key) => (
						<th key={key}>
							{/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */}
							{labels[key] ? (
								<TableHeaderButton onClick={() => headerOnClick(key)}>
									<TableHeaderIcon icon={sortedBy === key && reversed ? faChevronUp : faChevronDown} $active={sortedBy === key} />
									{/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */}
									{labels[key]}
								</TableHeaderButton>
							) : null}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{loading ? (
					<tr>
						<td colSpan={labelKeys.length + (action ? 1 : 0)}>
							<LoadingContainer>
								<LoadingAnimation />
							</LoadingContainer>
						</td>
					</tr>
				) : (
					sortedEntries.map((entry, i) => (
						<tr key={i}>
							{/* For every labelled key, format (if function provided) and insert into table */}
							{labelKeys.map((key) => (
								<td key={key}>
									{/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */}
									{generators?.[key] ? generators[key]!!(entry) : get(entry, key)}
								</td>
							))}
							{action && <td>{action(entry)}</td>}
						</tr>
					))
				)}
				{((!sortedEntries.length && !loading) || error) && (
					<tr>
						<td colSpan={100} style={{ textAlign: "center" }}>
							{error ? (
								<>
									<FontAwesomeIcon size="1x" color="darkred" icon={faXmarkCircle} /> Henting av data feilet
								</>
							) : (
								"Ingen data å vise"
							)}
						</td>
					</tr>
				)}
			</tbody>
		</TableRoot>
	);
}

const LoadingContainer = styled.div`
	margin: 0;
	padding: 0 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
`;
